<template>
  <div :class="bem.b()">
    <div :class="bem.e('wrapper')">
      <div ref="viewerContainer" :class="bem.e('view')">
        <markdown-viewer
          ref="markDownRef"
          :locale="zh"
          :value="currentValue"
          :plugins="plugins"
        />
      </div>
      <div
        v-if="props.showCatalog && cateList.length > 0"
        :class="bem.e('catalog')"
      >
        <h6>目录</h6>
        <ul :class="bem.e('catalog-list')">
          <li
            v-for="(item, index) in cateList"
            :key="index"
            :class="[{ active: anchor == index }, item.tagName + '-class']"
            class="marker-item"
            @click="scrollToSection('head-' + index, index)"
          >
            {{ item.text }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  computed,
  markRaw,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
//@ts-ignore
import breaks from '@bytemd/plugin-breaks'
import frontmatter from '@bytemd/plugin-frontmatter'
import gemoji from '@bytemd/plugin-gemoji'
import gfm from '@bytemd/plugin-gfm'
import highlight from '@bytemd/plugin-highlight'
import math from '@bytemd/plugin-math'
import mediumZoom from '@bytemd/plugin-medium-zoom'
import mermaid from '@bytemd/plugin-mermaid'
import * as BytemdVueNext from '@bytemd/vue-next'
import { createNamespace } from '@ll-plus/utils'
import { getProcessor } from 'bytemd'
import zhHans from 'bytemd/locales/zh_Hans.json'
import { debounce, findIndex, throttle } from 'lodash-es'
import { LlMarkdownPreviewerProps } from '../config'

defineOptions({ name: 'LlMarkdownPreviewer' })
const props = defineProps(LlMarkdownPreviewerProps)

const MarkdownViewer = (BytemdVueNext as any).Viewer
const markDownRef = ref()
const viewerContainer = ref()

const bem = createNamespace('markdown-previewer')
const pluginsList = [
  gfm(),
  gemoji(),
  highlight(),
  frontmatter(),
  mediumZoom(),
  breaks(),
  mermaid(),
  math(),
]

interface CatalogItem {
  tagName: string
  text: string
}

interface HeadingElement {
  element: Element
  index: number
  offsetTop: number
}

const state = reactive({
  plugins: markRaw(pluginsList),
  zh: zhHans,
  cateList: [] as CatalogItem[],
  anchor: 0,
  scrollContainer: null as Element | null,
  isManualScrolling: false, // 是否为手动点击滚动
  headingElements: [] as HeadingElement[], // 缓存标题元素信息
})

const { anchor, plugins, zh, cateList } = toRefs(state)

const currentValue = computed(() => props.value || '')

const getCataLogData = () => {
  try {
    getProcessor({
      plugins: [
        {
          rehype: (p) =>
            p.use(() => (tree) => {
              if (tree && tree.children && tree.children.length) {
                createCataLog(tree)
              }
            }),
        },
      ],
    }).processSync(currentValue.value)
  } catch (error) {
    console.error('处理目录数据时出错:', error)
  }
}

const createCataLog = (tree: any) => {
  const items: CatalogItem[] = []
  if (!tree.children) return

  tree.children
    .filter((v: any) => v.type === 'element')
    .forEach((node: any) => {
      if (
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.tagName) &&
        node.children &&
        node.children.length > 0
      ) {
        const text = stringifyHeading(node)
        if (text.trim()) {
          items.push({
            tagName: node.tagName,
            text: text.trim(),
          })
        }
      }
    })
  state.cateList = items
}

const stringifyHeading = (node: any): string => {
  let result = ''
  if (!node.children) return result

  node.children.forEach((item: any) => {
    if (item.type === 'text' && item.value) {
      result += item.value
    } else if (item.children) {
      result += stringifyHeading(item)
    }
  })
  return result
}

const transformToId = () => {
  try {
    const dom = document.querySelector('.markdown-body')
    if (!dom) return

    const children = Array.from(dom.children)
    if (children.length === 0) return

    children.forEach((child) => {
      if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(child.tagName)) {
        const index = findIndex(
          state.cateList,
          (v) => v.text === child.textContent?.trim(),
        )
        if (index >= 0) {
          child.setAttribute('id', `head-${index}`)
        }
      }
    })
  } catch (error) {
    console.error('设置ID时出错:', error)
  }
}

const scrollToSection = (sectionId: string, index: number) => {
  // 标记为手动滚动
  state.isManualScrolling = true
  state.anchor = index

  const section = document.getElementById(sectionId)
  if (section && state.scrollContainer) {
    const containerRect = state.scrollContainer.getBoundingClientRect()
    const sectionRect = section.getBoundingClientRect()
    const scrollTop = state.scrollContainer.scrollTop
    const targetPosition = scrollTop + sectionRect.top - containerRect.top - 20

    state.scrollContainer.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })

    // 滚动完成后重新启用自动更新
    setTimeout(() => {
      state.isManualScrolling = false
    }, 800) // 增加延迟确保平滑滚动完成
  }
}

// 获取滚动容器
const getScrollContainer = (): Element | null => {
  try {
    const markdownBody = document.querySelector('.markdown-body')
    if (!markdownBody) return null

    let element = markdownBody.parentElement
    while (element && element !== document.documentElement) {
      const style = window.getComputedStyle(element)
      const overflow = style.overflow + style.overflowY + style.overflowX

      if (
        /(auto|scroll)/.test(overflow) &&
        element.scrollHeight > element.clientHeight
      ) {
        return element
      }
      element = element.parentElement
    }

    return viewerContainer.value || document.documentElement
  } catch (error) {
    console.error('获取滚动容器时出错:', error)
    return document.documentElement
  }
}

// 缓存标题元素位置信息
const cacheHeadingElements = () => {
  try {
    if (!state.scrollContainer) return

    state.headingElements = state.cateList
      .map((_, index) => {
        const element = document.querySelector(`#head-${index}`)
        if (!element) return null

        return {
          element,
          index,
          offsetTop: 0, // 先初始化为0，在更新函数中计算
        }
      })
      .filter((item): item is HeadingElement => item !== null)

    updateHeadingPositions()
  } catch (error) {
    console.error('缓存标题元素时出错:', error)
  }
}

// 更新标题元素位置信息
const updateHeadingPositions = () => {
  try {
    if (!state.scrollContainer) return

    state.headingElements.forEach((item) => {
      const elementRect = item.element.getBoundingClientRect()
      const containerRect = state.scrollContainer!.getBoundingClientRect()
      // 计算相对于滚动容器的位置
      item.offsetTop =
        state.scrollContainer!.scrollTop + elementRect.top - containerRect.top
    })
  } catch (error) {
    console.error('更新位置信息时出错:', error)
  }
}

// 优化后的滚动处理函数 - 基于标题置顶原则
const handleScrollOptimized = throttle(() => {
  try {
    if (
      !state.scrollContainer ||
      state.headingElements.length === 0 ||
      state.isManualScrolling
    ) {
      return
    }

    const scrollTop = state.scrollContainer.scrollTop
    const containerHeight = state.scrollContainer.clientHeight
    const scrollHeight = state.scrollContainer.scrollHeight

    // 边界情况1：滚动到顶部时，激活第一项
    if (scrollTop <= 0) {
      if (state.anchor !== 0) {
        state.anchor = 0
      }
      return
    }

    // 边界情况2：滚动到底部时，激活最后一项
    if (scrollTop + containerHeight >= scrollHeight) {
      const lastIndex = state.headingElements.length - 1
      if (state.anchor !== lastIndex) {
        state.anchor = lastIndex
      }
      return
    }

    // 根据标题置顶原则确定当前激活的标题
    let activeIndex = 0
    const containerTop = state.scrollContainer.getBoundingClientRect().top

    // 找到最后一个已经到达或超过容器顶部的标题
    for (let i = 0; i < state.headingElements.length; i++) {
      const item = state.headingElements[i]
      const elementRect = item.element.getBoundingClientRect()
      const elementTop = elementRect.top

      // 如果标题已经到达或超过容器顶部，则更新activeIndex
      if (elementTop <= containerTop + 10) {
        // 10px的容差
        activeIndex = i
      } else {
        // 如果当前标题还没有到达顶部，则停止检查
        break
      }
    }

    // 只在anchor实际改变时更新
    if (state.anchor !== activeIndex) {
      state.anchor = activeIndex
    }
  } catch (error) {
    console.error('滚动处理时出错:', error)
  }
}, 16) // ~60fps

// 防抖的位置更新函数
const debouncedUpdatePositions = debounce(() => {
  updateHeadingPositions()
}, 100)

// 窗口大小改变处理
const handleResize = debounce(() => {
  updateHeadingPositions()
}, 200)

// 清理函数
const cleanup = () => {
  try {
    state.scrollContainer?.removeEventListener('scroll', handleScrollOptimized)
    window.removeEventListener('resize', handleResize)

    // 清理throttle和debounce
    handleScrollOptimized.cancel?.()
    debouncedUpdatePositions.cancel?.()
    handleResize.cancel?.()
  } catch (error) {
    console.error('清理时出错:', error)
  }
}

// 初始化滚动监听
const initScrollListener = () => {
  try {
    if (!state.scrollContainer) return

    // 缓存标题元素
    cacheHeadingElements()

    // 添加滚动监听
    state.scrollContainer.addEventListener('scroll', handleScrollOptimized, {
      passive: true,
    })

    // 添加窗口大小改变监听
    window.addEventListener('resize', handleResize, {
      passive: true,
    })

    // 初始设置anchor
    state.anchor = 0
  } catch (error) {
    console.error('初始化滚动监听时出错:', error)
  }
}

onMounted(async () => {
  try {
    getCataLogData()
    await nextTick()
    transformToId()
    await nextTick()

    // 获取滚动容器并初始化
    state.scrollContainer = getScrollContainer()
    initScrollListener()
  } catch (error) {
    console.error('组件挂载时出错:', error)
  }
})

onUnmounted(() => {
  cleanup()
})

watch(
  () => props.value,
  async (newVal) => {
    if (newVal !== undefined) {
      // 清理旧的监听器
      cleanup()

      getCataLogData()
      await nextTick()
      transformToId()
      await nextTick()

      state.scrollContainer = getScrollContainer()
      initScrollListener()
    }
  },
  { immediate: true },
)
</script>
